import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import { useCacheScope } from "@/hooks/use-cache-scope";
import type { TenantUser } from "@/lib/project-types";
import { cacheScope, subscribeCacheScope } from "@/lib/ui-cache";
import { fetchWithAuth, type AccessTokenGetter } from "@/lib/utils";
import i18n from "@/lib/i18n";

const TENANT_USERS_API = "/api/tenant-users";
const TTL = 5 * 60_000;

interface Store {
  users: TenantUser[] | null;
  fetchedAt: number;
  inFlight: Promise<TenantUser[]> | null;
  error: string | null;
}

const empty = (): Store => ({ users: null, fetchedAt: 0, inFlight: null, error: null });
let currentScope = cacheScope();
let store = empty();
let tokenGetter: AccessTokenGetter | null = null;
const listeners = new Set<() => void>();
const notify = () => { for (const listener of listeners) listener(); };
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
const getSnapshot = () => store;

function checkScope(announce = true): void {
  const next = cacheScope();
  if (next !== currentScope) {
    currentScope = next;
    store = empty();
    if (announce) notify();
  }
}
subscribeCacheScope(() => checkScope());

/** The caller supplies its token getter through useTenantUsers before requesting a refresh. */
export function ensureTenantUsersLoaded(force = false): Promise<TenantUser[]> {
  checkScope();
  if (store.inFlight) return store.inFlight;
  if (!force && store.users && Date.now() - store.fetchedAt < TTL) return Promise.resolve(store.users);
  const getToken = tokenGetter;
  if (!getToken) return Promise.reject(new Error("Tenant users token getter unavailable"));

  const request = (async () => {
    const response = await fetchWithAuth(TENANT_USERS_API, { headers: {} }, getToken);
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    const data: unknown = await response.json();
    if (!Array.isArray(data)) throw new Error(i18n.t("projects:usersInvalidResponse"));
    return data as TenantUser[];
  })();
  store = { ...store, inFlight: request, error: null };
  notify();
  void request.then(
    (users) => {
      if (currentScope !== cacheScope() || store.inFlight !== request) return;
      store = { users, fetchedAt: Date.now(), inFlight: null, error: null };
      notify();
    },
    (reason: unknown) => {
      if (currentScope !== cacheScope() || store.inFlight !== request) return;
      store = { ...store, inFlight: null, error: reason instanceof Error ? reason.message : i18n.t("projects:usersLoadFailed") };
      notify();
    },
  );
  return request;
}

export interface UseTenantUsersReturn {
  tenantUsers: TenantUser[];
  loading: boolean;
  error: string | null;
  hasData: boolean;
  refresh: () => Promise<TenantUser[]>;
}

export function useTenantUsers(getToken: AccessTokenGetter): UseTenantUsersReturn {
  useCacheScope();
  checkScope(false);
  tokenGetter = getToken;
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const backendReconnectKey = useBackendReconnectKey();
  const previousReconnectKey = useRef(backendReconnectKey);
  useEffect(() => {
    if (backendReconnectKey !== previousReconnectKey.current) {
      previousReconnectKey.current = backendReconnectKey;
      store = empty();
      notify();
    }
  }, [backendReconnectKey]);
  const refresh = useCallback(() => ensureTenantUsersLoaded(), []);
  return {
    tenantUsers: snapshot.users ?? [],
    loading: !snapshot.users && Boolean(snapshot.inFlight),
    error: snapshot.error,
    hasData: snapshot.users !== null,
    refresh,
  };
}
